import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import {
  Building2, Wrench, ClipboardList, ShieldAlert, Users, Radio, Loader2, ArrowRight, Activity, CheckCircle2, AlertTriangle,
} from "lucide-react";
import {
  ResponsiveContainer, BarChart, Bar, Cell, CartesianGrid, XAxis, YAxis, Tooltip,
} from "recharts";
import SwingProgressTracker from "@/components/SwingProgressTracker";

const ORANGE = "#f58220";
const NAVY = "#3b82f6";
const RED = "#ef4444";
const GREEN = "#22c55e";

/**
 * SUPERINTENDENT view — everything, at a glance. Command centre.
 */
export default function SuperintendentDashboard() {
  const { user } = useAuth();
  const [d, setD] = useState({ loading: true });

  useEffect(() => {
    (async () => {
      const results = await Promise.allSettled([
        api.get("/handovers"),
        api.get("/breakdowns"),
        api.get("/inspections"),
        api.get("/hazards/pending"),
        api.get("/assets"),
        api.get("/admin/users").catch(() => ({ data: [] })),
        api.get("/admin/sites").catch(() => ({ data: [] })),
      ]);
      const list = (r) => {
        if (r.status !== "fulfilled") return [];
        const d = r.value.data;
        return Array.isArray(d) ? d : d?.items || d?.results || d?.data || [];
      };
      setD({
        loading: false,
        handovers: list(results[0]),
        breakdowns: list(results[1]),
        inspections: list(results[2]),
        hazards: list(results[3]),
        assets: list(results[4]),
        users: list(results[5]),
        sites: list(results[6]),
      });
    })().catch((ex) => setD({ loading: false, error: apiError(ex) }));
  }, []);

  if (d.loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center gap-3 text-muted">
        <Loader2 className="w-5 h-5 animate-spin" /> Loading operation overview…
      </div>
    );
  }

  // By-site breakdown counts
  const bySite = groupCount(d.breakdowns, (b) => b.site_code || b.site || "Unassigned");
  const bySiteRows = Object.entries(bySite).sort((a, b) => b[1] - a[1]).slice(0, 8)
    .map(([site, count]) => ({ site: String(site).slice(0, 20), count }));

  // Compliance
  const inspTotal = d.inspections.length;
  const inspClosed = d.inspections.filter((i) => ["closed", "done", "complete", "approved"].includes(String(i.status || "").toLowerCase())).length;
  const compliancePct = inspTotal ? Math.round((inspClosed / inspTotal) * 100) : 0;

  const activeBreakdowns = d.breakdowns.filter((b) => !["closed", "resolved", "done"].includes(String(b.status || "").toLowerCase()));

  return (
    <div className="space-y-8" data-testid="superintendent-dashboard">
      {/* Hero */}
      <div className="panel-heavy p-6 md:p-8 flex flex-col md:flex-row md:items-center gap-6 justify-between">
        <div>
          <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.3em] font-black text-muted">
            <Radio className="w-3.5 h-3.5" style={{ color: ORANGE }} />
            Superintendent · Operation-wide
          </div>
          <h1 className="font-display text-4xl md:text-5xl font-black tracking-tight leading-[1.05] mt-2">
            The whole operation,<br className="hidden md:block" />
            <span style={{ color: ORANGE }}>one screen.</span>
          </h1>
          <p className="text-muted mt-3 max-w-xl">
            Live signal across every site, team and asset.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          {user?.role && <div className="pill pill-orange">{user.role}</div>}
        </div>
      </div>

      {/* Big overview strip */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Big label="Sites" value={d.sites.length || uniqueCount(d.handovers.concat(d.breakdowns), (x) => x.site_code || x.site)} icon={Building2} />
        <Big label="Active Breakdowns" value={activeBreakdowns.length} tone={activeBreakdowns.length ? "hazard" : undefined} icon={Wrench} />
        <Big label="Hazards Pending" value={d.hazards.length} tone={d.hazards.length ? "danger" : undefined} icon={ShieldAlert} />
        <Big label="Compliance" value={`${compliancePct}%`} icon={CheckCircle2} sub={`${inspClosed}/${inspTotal} inspections closed`} />
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Big label="Team Size" value={d.users.length} icon={Users} />
        <Big label="Handovers · 24h" value={countLastHours(d.handovers, 24)} icon={ClipboardList} />
        <Big label="Breakdowns · 7d" value={countLastHours(d.breakdowns, 24 * 7)} icon={Activity} />
        <Big label="Assets on Register" value={d.assets.length} icon={Building2} />
      </div>

      {/* Chart + hazard alerts */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <section className="panel-heavy p-5 lg:col-span-2">
          <div className="flex items-center gap-2 mb-3">
            <Wrench className="w-4 h-4 text-primary-brand" />
            <div className="font-display font-black uppercase tracking-widest text-sm">Breakdowns by site</div>
          </div>
          {bySiteRows.length ? (
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={bySiteRows} margin={{ top: 8, right: 8, left: 0, bottom: 0 }} layout="vertical">
                <CartesianGrid strokeDasharray="3 6" stroke="var(--border)" />
                <XAxis type="number" allowDecimals={false} />
                <YAxis dataKey="site" type="category" width={140} />
                <Tooltip />
                <Bar dataKey="count" radius={[0, 8, 8, 0]}>
                  {bySiteRows.map((_, i) => (
                    <Cell key={i} fill={i === 0 ? RED : i === 1 ? ORANGE : i === 2 ? NAVY : "#94a3b8"} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="text-muted text-sm py-8 text-center">No breakdown data.</div>
          )}
        </section>

        <section className="panel-accent p-5">
          <div className="flex items-center gap-2 mb-3">
            <ShieldAlert className="w-4 h-4 text-primary-brand" />
            <div className="font-display font-black uppercase tracking-widest text-sm">Hazard alerts</div>
          </div>
          {d.hazards.length === 0 ? (
            <div className="flex items-center gap-2 text-sm py-3" style={{ color: GREEN }}>
              <CheckCircle2 className="w-4 h-4" /> No pending hazards.
            </div>
          ) : (
            <div className="divide-border">
              {d.hazards.slice(0, 6).map((h, i) => (
                <div key={i} className="py-3 first:pt-0">
                  <div className="flex items-center gap-2 mb-1">
                    {h.severity && <span className={`pill ${sevClass(h.severity)}`}>{h.severity}</span>}
                    {h.site_code && <span className="pill">{h.site_code}</span>}
                  </div>
                  <div className="font-display font-black text-sm truncate">
                    {h.title || h.description || h.summary}
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>
      </div>

      {/* Live swing progress across the operation */}
      <SwingProgressTracker />

      {/* Team activity */}
      <section className="panel-heavy p-5">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <Users className="w-4 h-4 text-primary-brand" />
            <div className="font-display font-black uppercase tracking-widest text-sm">Team activity · last 7 days</div>
          </div>
          <Link to="/admin/users" className="text-[11px] uppercase tracking-widest text-muted hover:text-white">
            Roster <ArrowRight className="inline w-3.5 h-3.5" />
          </Link>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {topContributors(d.breakdowns.concat(d.handovers, d.inspections), 8).map(({ name, count }) => (
            <div key={name} className="panel-flat p-3 flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl flex items-center justify-center font-display font-black text-base"
                   style={{ background: "rgba(245,130,32,0.15)", color: ORANGE, border: "1px solid rgba(245,130,32,0.3)" }}>
                {initials(name)}
              </div>
              <div className="min-w-0">
                <div className="font-display font-black text-sm truncate">{name}</div>
                <div className="text-[10px] uppercase tracking-widest text-muted">
                  {count} entries
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      <div className="pt-2 flex flex-wrap items-center justify-center gap-3">
        <Link to="/insights" className="btn btn-primary btn-sm">Open Performance</Link>
        <Link to="/handovers" className="btn btn-ghost btn-sm">Operational Handover</Link>
        <Link to="/breakdowns" className="btn btn-ghost btn-sm">Asset Breakdowns</Link>
        <Link to="/hazards" className="btn btn-ghost btn-sm">Site Hazards</Link>
      </div>
    </div>
  );
}

function Big({ label, value, sub, icon: Icon, tone }) {
  const styles =
    tone === "hazard"
      ? { background: "linear-gradient(180deg, #ffb15c, #f58220)", color: "#0a0a0a", borderColor: "#f58220" }
      : tone === "danger"
      ? { background: "linear-gradient(180deg, #ef4444, #b91c1c)", color: "#fff", borderColor: "#ef4444" }
      : {};
  const iconStyle =
    tone === "hazard"
      ? { background: "#0a0a0a", color: ORANGE }
      : tone === "danger"
      ? { background: "#fff", color: "#b91c1c" }
      : { background: "rgba(245,130,32,0.15)", color: ORANGE, border: "1px solid rgba(245,130,32,0.3)" };
  return (
    <div className="panel-heavy p-5 min-h-[140px] flex flex-col justify-between" style={styles}>
      <div className="flex items-start justify-between">
        <div className="text-[10px] uppercase tracking-[0.2em] font-black">{label}</div>
        <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={iconStyle}>
          <Icon className="w-5 h-5" />
        </div>
      </div>
      <div>
        <div className="font-display font-black text-4xl leading-none tracking-tight">{value ?? "—"}</div>
        {sub && <div className="text-[10px] uppercase tracking-widest mt-2 opacity-80">{sub}</div>}
      </div>
    </div>
  );
}

function groupCount(list, keyFn) {
  const o = {};
  (list || []).forEach((it) => {
    const k = keyFn(it) || "unknown";
    o[k] = (o[k] || 0) + 1;
  });
  return o;
}
function uniqueCount(list, keyFn) {
  const s = new Set();
  (list || []).forEach((it) => { const k = keyFn(it); if (k) s.add(k); });
  return s.size;
}
function countLastHours(list, hours) {
  const cutoff = Date.now() - hours * 3600 * 1000;
  return (list || []).filter((it) => {
    const t = it.created_at || it.date || it.ts;
    return t && new Date(t).getTime() >= cutoff;
  }).length;
}
function topContributors(list, n) {
  const m = new Map();
  (list || []).forEach((it) => {
    const name = it.created_by_name || it.reported_by || it.inspector_name || it.user;
    if (!name) return;
    m.set(name, (m.get(name) || 0) + 1);
  });
  return [...m.entries()].sort((a, b) => b[1] - a[1]).slice(0, n).map(([name, count]) => ({ name, count }));
}
function initials(name) {
  return String(name || "?").split(/\s+/).map((p) => p[0]).join("").slice(0, 2).toUpperCase();
}
function sevClass(s) {
  const v = String(s || "").toLowerCase();
  if (v === "critical") return "pill-red-solid";
  if (v === "high") return "pill-orange-solid";
  if (v === "medium") return "pill-blue";
  return "pill";
}
